import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Dialogo modal sobre <dialog> nativo (foco preso, Esc fecha, fundo inerte) — no celular abre como
   folha presa ao rodape; em telas largas, centralizado. Sem dependencia nova. */
export function Dialog({ open, onClose, title, description, children, hideTitle = false, className, bodyClassName }: {
    open: boolean
    onClose: () => void
    title: string
    description?: string
    children: ReactNode
    hideTitle?: boolean
    className?: string
    bodyClassName?: string
}) {
    const { t } = useTranslation()
    const ref = useRef<HTMLDialogElement>(null)
    const titleId = useId()
    const descId = useId()

    useEffect(() => {
        const dialog = ref.current
        if (!dialog) return
        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    return (
        <dialog
            ref={ref}
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            onCancel={(e) => { e.preventDefault(); onClose() }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
            className={cn(
                'mx-auto mt-auto mb-0 max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-[1.75rem] border-0 bg-surface p-0 text-ink shadow-xl backdrop:bg-ink/55',
                'sm:m-auto sm:w-[min(100%-2rem,28rem)] sm:rounded-card',
                className,
            )}
        >
            {open ? (
                <div className={cn('flex flex-col gap-4 px-6 pt-5 pb-[calc(1.5rem+var(--spacing-safe-bottom))]', bodyClassName)}>
                    <div className={cn('flex items-start gap-2', hideTitle && 'absolute top-3 right-3 z-10')}>
                        <h2 id={titleId} className={cn('m-0 grow font-display text-xl font-bold', hideTitle && 'sr-only')}>{title}</h2>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t('common.close')}
                            className={cn(
                                '-mt-1 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full border-0 hover:bg-ink/5',
                                hideTitle ? 'bg-ink/55 text-white hover:bg-ink/70' : 'bg-transparent text-ink',
                            )}
                        >
                            <X className="size-5" aria-hidden="true" />
                        </button>
                    </div>
                    {description ? <p id={descId} className="m-0 -mt-2 text-[0.9rem] leading-relaxed text-ink-muted">{description}</p> : null}
                    {children}
                </div>
            ) : null}
        </dialog>
    )
}
